// For web-only React Native projects, hardcoding the version is the most reliable approach
// This should match the version in package.json and app.json
const APP_VERSION = "2.0.0";

/**
 * Get the current app version
 * @returns {string} The version string (e.g., "1.24.0")
 */
export const getAppVersion = () => {
  return APP_VERSION;
};

/**
 * Get the formatted version string with 'v' prefix
 * @returns {string} The formatted version (e.g., "v1.24.0")
 */
export const getFormattedVersion = () => {
  return `v${getAppVersion()}`;
};

/**
 * Compare two version strings
 * @param {string} version1 - First version (e.g., "1.24.0")
 * @param {string} version2 - Second version (e.g., "1.23.0")
 * @returns {number} 1 if version1 > version2, -1 if version1 < version2, 0 if equal
 */
export const compareVersions = (version1, version2) => {
  const v1Parts = version1.replace(/^v/, "").split(".").map(Number);
  const v2Parts = version2.replace(/^v/, "").split(".").map(Number);

  for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
    const v1Part = v1Parts[i] || 0;
    const v2Part = v2Parts[i] || 0;

    if (v1Part > v2Part) return 1;
    if (v1Part < v2Part) return -1;
  }

  return 0;
};

/**
 * Check if current version is greater than or equal to a minimum version
 * @param {string} minVersion - Minimum version to check (e.g., "1.20.0")
 * @returns {boolean} True if current version >= minVersion
 */
export const isVersionAtLeast = (minVersion) => {
  const currentVersion = getAppVersion();
  return compareVersions(currentVersion, minVersion) >= 0;
};

/**
 * Check if current version is less than a maximum version
 * @param {string} maxVersion - Maximum version to check (e.g., "1.25.0")
 * @returns {boolean} True if current version < maxVersion
 */
export const isVersionBelow = (maxVersion) => {
  const currentVersion = getAppVersion();
  return compareVersions(currentVersion, maxVersion) < 0;
};

/**
 * Check if current version is within a range
 * @param {string} minVersion - Minimum version (inclusive)
 * @param {string} maxVersion - Maximum version (exclusive)
 * @returns {boolean} True if minVersion <= current version < maxVersion
 */
export const isVersionInRange = (minVersion, maxVersion) => {
  return isVersionAtLeast(minVersion) && isVersionBelow(maxVersion);
};
